import { IconMenu } from '@components/Icon';
import useImpersonate from '@State/Impersonate/useImpersonate';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RRDLink } from 'react-router-dom';
import logotype from '../../assets/logotype/remondis-logo.svg';
import useClickOutside from '../../hooks/useClickOutside';
import useAuth from '../../State/Auth/useAuth';
import { SidebarCtx } from './SidebarContext';
import SidebarNavigationList from './SidebarNavigationList';
import SidebarProfile from './SidebarProfile';
import {
  HamburgerButton,
  InnerNav,
  Logo,
  LogoImg,
  MobileHeader,
  MobileLogo,
  Nav,
  SidebarContainer,
  SidebarProfileWrapper,
  StickyHeader,
} from './styles';

interface SidebarProps {}

const Sidebar: React.FC<SidebarProps> = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { parsedToken, authenticated } = useAuth();
  const { impersonate } = useImpersonate();
  const { closeSubNav, showMobileMenu, toggleMobileMenu } = useContext(SidebarCtx);

  const [isAdmin, setIsAdmin] = useState<{ clientAdmin: boolean; userAdmin: boolean }>({
    clientAdmin: !parsedToken?.clientadmin,
    userAdmin: !!parsedToken?.useradmin,
  });

  useClickOutside(wrapperRef, closeSubNav);

  useEffect(() => {
    if (impersonate) {
      setIsAdmin({ clientAdmin: impersonate?.clientAdmin, userAdmin: false });
    } else {
      setIsAdmin({
        clientAdmin: parsedToken?.clientadmin ? parsedToken?.clientadmin : false,
        userAdmin: parsedToken?.useradmin ? parsedToken?.useradmin : false,
      });
    }
  }, [impersonate, parsedToken]);

  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileMenu]);

  if (!authenticated) {
    return null;
  }

  return (
    <>
      <MobileHeader $impersonate={!!impersonate}>
        <MobileLogo>
          <RRDLink
            to="/"
            title={t('navigation_home_title')}
            onClick={() => {
              if (showMobileMenu) {
                toggleMobileMenu();
              }
            }}
          >
            <img src={logotype} alt={t('header_logotype_alt')} />
          </RRDLink>
        </MobileLogo>
        <HamburgerButton onClick={toggleMobileMenu} aria-label="Toggle menu">
          <IconMenu />
        </HamburgerButton>
      </MobileHeader>

      <SidebarContainer
        $impersonate={!!impersonate}
        className={showMobileMenu ? 'mobile-open' : ''}
      >
        <Nav
          aria-label="Sidebar navigation"
          ref={wrapperRef}
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          <StickyHeader>
            <Logo>
              <RRDLink to="/" title={t('navigation_home_title')}>
                <LogoImg src={logotype} alt={t('header_logotype_alt')} />
              </RRDLink>
            </Logo>
          </StickyHeader>

          <InnerNav style={{ flex: 1, minHeight: 0 }}>
            <SidebarNavigationList
              userAdmin={isAdmin.userAdmin}
              clientAdmin={isAdmin.clientAdmin}
            />
          </InnerNav>

          <SidebarProfileWrapper>
            <SidebarProfile />
          </SidebarProfileWrapper>
        </Nav>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
